/**
 * Public Rent Flow — ESS-style 3-step wizard.
 *
 * Route: /rent/:slug
 *
 * Step 1 (RentList):   facility header + unit_type grid with "Rent Now"
 * Step 2 (UnitPick):   modal — payment options + Select unit dropdown
 * Step 3 (RentWizard): 3-step form (Personal / Payment / Review) + sticky
 *                      right-rail Due Today summary
 *
 * No auth required. Subdomain hint isn't relied on — the URL slug is
 * the source of truth.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { usePublicFacility } from '../context/PublicFacilityContext';
import {
  MapPin, Phone, X, ChevronRight, ShieldCheck, AlertTriangle,
  CheckCircle2, RefreshCw, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

const money = (v) => {
  const n = Number(v || 0);
  return `$${n.toFixed(2)}`;
};

// ---------------------------------------------------------------------------
// Step 2 modal
// ---------------------------------------------------------------------------

const UnitPickModal = ({ open, unitType, onClose, onRent }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [error, setError] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!unitType) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API_URL}/api/rent/${unitType.facility_id}/unit-type/${unitType.id}`
      );
      setDetail(r.data);
      const first = r.data?.available_units?.[0];
      setSelectedUnitId(first ? first.id : '');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [unitType]);

  useEffect(() => {
    if (open) loadDetail();
  }, [open, loadDetail]);

  if (!open || !unitType) return null;

  const u = detail?.unit_type || unitType;
  const monthly = u.monthly_rate || 0;
  const available = detail?.available_units || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">
            {u.name}
            {(u.width && u.length) ? <span className="text-slate-400 text-sm font-normal ml-2">({u.width} x {u.length})</span> : null}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          {u.photo_url && (
            <img src={u.photo_url} alt={u.name} className="w-full h-48 object-cover rounded-lg mb-4" />
          )}
          {u.description && (
            <p className="text-[14px] text-slate-600 leading-relaxed mb-5">{u.description}</p>
          )}

          <div className="mb-5">
            <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Payment options</h3>
            <label className="flex items-center gap-3 border border-blue-200 bg-blue-50/40 rounded-lg p-3 cursor-pointer">
              <input type="radio" defaultChecked className="h-4 w-4" />
              <div>
                <div className="text-[13px] font-medium text-slate-900">1 Month for {money(monthly)}</div>
                <div className="text-[11px] text-slate-500">{money(monthly)} / month</div>
              </div>
            </label>
          </div>

          <div className="mb-5">
            <h3 className="text-[13px] font-semibold text-slate-700 mb-2">Select unit</h3>
            {loading ? (
              <p className="text-[13px] text-slate-400">Loading available units...</p>
            ) : available.length === 0 ? (
              <p className="text-[13px] text-amber-700 bg-amber-50/50 border border-amber-200 rounded p-2.5">
                No units of this type are currently available. Please call the facility.
              </p>
            ) : (
              <select
                value={selectedUnitId}
                onChange={(e) => setSelectedUnitId(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-[14px]"
              >
                {available.map((u) => (
                  <option key={u.id} value={u.id}>{u.unit_number}</option>
                ))}
              </select>
            )}
          </div>

          {error && (
            <p className="text-[12px] text-rose-700 mb-3">{error}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 hover:bg-slate-100 rounded text-[13px] text-slate-700"
          >
            Close
          </button>
          <button
            onClick={() => onRent(u, selectedUnitId)}
            disabled={!selectedUnitId}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-[13px] font-medium"
          >
            Rent Now <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1 listing — entry point
// ---------------------------------------------------------------------------

const RentFlow = () => {
  const params = useParams();
  // Slug comes either from the URL (/rent/:slug) or from the facility
  // subdomain ({facility}.mysms360.com/rent -> "{facility}" via the public
  // facility context). Subdomain-based slug is the production case for
  // facility websites.
  const { slug: subdomainSlug } = usePublicFacility() || {};
  const slug = params.slug || subdomainSlug;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [stage, setStage] = useState('list'); // 'list' | 'wizard'
  const [selection, setSelection] = useState(null); // {unit_type, unit_id, unit_number}

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API_URL}/api/rent/by-slug/${slug}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const onPickUnit = (unitType) => {
    setActiveType({ ...unitType, facility_id: data.facility.id });
    setModalOpen(true);
  };

  const onRent = async (unitType, unitId) => {
    // Resolve unit_number from the modal's loaded detail.
    let unitNumber = '';
    try {
      const r = await axios.get(
        `${API_URL}/api/rent/${data.facility.id}/unit-type/${unitType.id}`
      );
      const u = (r.data?.available_units || []).find((x) => x.id === unitId);
      unitNumber = u?.unit_number || '';
    } catch {}
    setSelection({
      facility: data.facility,
      unit_type: unitType,
      unit_id: unitId,
      unit_number: unitNumber,
    });
    setModalOpen(false);
    setStage('wizard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Facility not found</h1>
          <p className="text-[13px] text-slate-500">{error || `We couldn't load "${slug}". Check the URL or contact the facility.`}</p>
        </div>
      </div>
    );
  }

  // Lazy import wizard (keeps this file scannable).
  if (stage === 'wizard' && selection) {
    const RentWizard = require('./RentWizard').default;
    return (
      <RentWizard
        selection={selection}
        onBack={() => setStage('list')}
      />
    );
  }

  const f = data.facility;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">{f.name}</h1>
            <p className="text-[12px] text-slate-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {f.address}, {f.city}, {f.state} {f.zip_code}
              {f.phone ? <span className="ml-3"><Phone className="h-3 w-3 inline" /> {f.phone}</span> : null}
            </p>
          </div>
          <Link
            to={`/portal`}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-[13px] font-medium"
          >
            Make a Payment / Login
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-semibold text-slate-900 text-center mb-8">Rent Storage</h2>

        {!f.stripe_ready && (
          <div className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-4 mb-6 max-w-3xl mx-auto">
            <p className="text-[12px] text-amber-700">
              Online payment isn't connected yet. You can still reserve — the facility will reach out to collect payment.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data.unit_types || []).filter((t) => t.available > 0 || t.available === undefined).length === 0 && (
            <div className="md:col-span-2 text-center text-slate-500 text-[13px]">
              No units currently available. Please check back soon or call the facility.
            </div>
          )}
          {(data.unit_types || []).map((t) => (
            <article key={t.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-sm transition-shadow">
              <div className="p-5">
                <h3 className="text-[16px] font-semibold text-slate-900 mb-3">
                  {t.name}
                  {(t.width && t.length) ? <span className="text-slate-400 text-sm font-normal ml-1">({t.width} x {t.length})</span> : null}
                </h3>
                <div className="flex gap-4">
                  {t.photo_url && (
                    <img src={t.photo_url} alt={t.name} className="w-32 h-24 object-cover rounded-lg flex-shrink-0" />
                  )}
                  <p className="text-[13px] text-slate-600 leading-relaxed line-clamp-5">{t.description || 'Storage unit available for rent.'}</p>
                </div>
                <div className="flex items-end justify-between mt-5">
                  <div>
                    <div className="text-[20px] font-bold text-slate-900">{money(t.monthly_rate)} <span className="text-[13px] font-normal text-slate-500">/ month</span></div>
                    {t.available > 0 ? (
                      <div className="text-[11px] text-emerald-600">{t.available} available</div>
                    ) : (
                      <div className="text-[11px] text-rose-600">Not available</div>
                    )}
                  </div>
                  <button
                    onClick={() => onPickUnit(t)}
                    disabled={!t.available}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded text-[13px] font-medium"
                  >
                    Rent Now
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>

      <UnitPickModal
        open={modalOpen}
        unitType={activeType}
        onClose={() => setModalOpen(false)}
        onRent={onRent}
      />
    </div>
  );
};

export default RentFlow;
